import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { SectionHeading } from './ui/SectionHeading';
import { ScrollReveal } from './ui/ScrollReveal';
import { Button } from './ui/Button';
const BRANDS = {
  monopoly: {
    name: 'Monopoly',
    color: '#CC0000',
    tagline: 'Navigate the board. Own every hole.'
  },
  hotwheels: {
    name: 'Hot Wheels',
    color: '#FF6600',
    tagline: 'High-speed loops and legendary tracks.'
  },
  pawpatrol: {
    name: 'PAW Patrol',
    color: '#2196F3',
    tagline: 'Adventure-ready fun for little putters.'
  },
  barbie: {
    name: 'Barbie',
    color: '#E91E90',
    tagline: 'Glamour, style, and pink-carpet putting.'
  },
  rubiks: {
    name: "Rubik's Cube",
    color: '#FFD700',
    tagline: 'Twist, solve, and putt through the puzzle.'
  },
  peppa: {
    name: 'Peppa Pig',
    color: '#FF9EC2',
    tagline: 'Muddy puddles meet mini putt magic.'
  },
  thomas: {
    name: 'Thomas & Friends',
    color: '#1565C0',
    tagline: 'Full steam ahead through every hole.'
  },
  nerf: {
    name: 'Nerf',
    color: '#FF8C00',
    tagline: 'Blast through obstacles. Compete to win.'
  },
  transformers: {
    name: 'Transformers',
    color: '#7B1FA2',
    tagline: 'Roll out through epic design worlds.'
  }
};
const CATEGORIES = {
  'Best Overall': ['monopoly', 'hotwheels', 'pawpatrol', 'barbie', 'rubiks', 'peppa', 'thomas', 'nerf'],
  'Family Resort': ['pawpatrol', 'peppa', 'thomas', 'barbie', 'hotwheels'],
  'Premium & Viral': ['barbie', 'hotwheels', 'monopoly', 'rubiks'],
  Competitive: ['monopoly', 'nerf', 'rubiks', 'hotwheels'],
  'Signature Holes': ['hotwheels', 'monopoly', 'barbie', 'rubiks', 'transformers']
};
type CategoryType = keyof typeof CATEGORIES;
export function LicensedExperiences() {
  const [activeCategory, setActiveCategory] = useState<CategoryType>('Best Overall');
  return <section className="py-32 bg-brand-green-deep text-brand-cream-light relative overflow-hidden" data-id="element-87">
      {/* Background patterns */}
      <div className="contour-pattern absolute inset-0 opacity-10 pointer-events-none" data-id="element-88" />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(184,150,90,0.15)_0%,transparent_50%)] pointer-events-none" data-id="element-89" />

      <div className="max-w-7xl mx-auto px-6 relative z-10" data-id="element-90">
        <SectionHeading eyebrow="Licensed Experiences" title="Bring iconic brands to life on the green." subtitle="From family favorites to high-speed challenges, PuttLab designs world-class licensed mini putt experiences featuring the world's most recognized IP." light className="mb-16" data-id="element-91" />

        <ScrollReveal delay={0.2} data-id="element-92">
          {/* Pill Filter */}
          <div className="flex overflow-x-auto pb-4 mb-12 gap-3 [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]" data-id="element-93">
            {(Object.keys(CATEGORIES) as CategoryType[]).map(cat => <button key={cat} onClick={() => setActiveCategory(cat)} className={`whitespace-nowrap px-6 py-2.5 rounded-full font-body text-sm font-medium transition-all duration-300 ${activeCategory === cat ? 'bg-brand-gold text-brand-charcoal shadow-[0_0_15px_rgba(184,150,90,0.4)]' : 'bg-brand-cream-light/5 text-brand-cream/70 border border-brand-cream/10 hover:bg-brand-cream-light/10 hover:text-brand-cream-light'}`} data-id="element-94">
                {cat}
              </button>)}
          </div>

          {/* Grid */}
          <motion.div layout className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6" data-id="element-95">
            <AnimatePresence mode="popLayout" data-id="element-96">
              {CATEGORIES[activeCategory].map((brandId, index) => {
              const brand = BRANDS[brandId as keyof typeof BRANDS];
              return <motion.div key={brandId} layout initial={{
                opacity: 0,
                scale: 0.9
              }} animate={{
                opacity: 1,
                scale: 1
              }} exit={{
                opacity: 0,
                scale: 0.9
              }} transition={{
                duration: 0.3,
                delay: index * 0.05
              }} className="glass-panel rounded-xl overflow-hidden flex flex-col group hover:border-brand-gold/30 transition-colors duration-300 relative" data-id="element-97">
                    {/* Brand color gradient wash */}
                    <div className="absolute inset-0 opacity-[0.07] group-hover:opacity-[0.12] transition-opacity duration-500 pointer-events-none" style={{
                  background: `radial-gradient(circle at top left, ${brand.color} 0%, transparent 70%)`
                }} data-id="element-98" />
                    {/* Top accent bar */}
                    <div className="h-1 w-full" style={{
                  backgroundColor: brand.color
                }} data-id="element-99" />
                    <div className="p-5 md:p-6 flex-1 flex flex-col relative z-10" data-id="element-100">
                      {/* Brand-colored left accent line */}
                      <div className="w-8 h-[3px] rounded-full mb-4" style={{
                    backgroundColor: brand.color
                  }} data-id="element-101" />
                      <h3 className="text-xl md:text-2xl font-heading leading-tight mb-3 transition-colors duration-300" style={{
                    color: brand.color
                  }} data-id="element-102">
                        {brand.name}
                      </h3>
                      <p className="text-xs md:text-sm font-body text-brand-cream/60 leading-relaxed flex-1" data-id="element-103">
                        {brand.tagline}
                      </p>
                    </div>
                  </motion.div>;
            })}
            </AnimatePresence>
          </motion.div>
        </ScrollReveal>

        {/* Bottom CTA */}
        <ScrollReveal delay={0.4} data-id="element-104">
          <div className="mt-20 text-center border-t border-brand-cream/10 pt-12" data-id="element-105">
            <h3 className="text-2xl md:text-3xl font-heading text-brand-cream-light mb-6" data-id="element-106">
              Have a brand in mind? We'll design the experience.
            </h3>
            <Button variant="primary" href="/contact" data-id="element-107">
              Discuss Your Concept
            </Button>
          </div>
        </ScrollReveal>
      </div>
    </section>;
}